import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { fetchFranjasHorarias, fetchOcupacionAulas, fetchAulasActivas } from "../Guardias/Guardias-Api";

const DIAS = [
  { id: 1, descripcion: "Lunes" },
  { id: 2, descripcion: "Martes" },
  { id: 3, descripcion: "Miércoles" },
  { id: 4, descripcion: "Jueves" },
  { id: 5, descripcion: "Viernes" },
];

const AulasLibres = ({ setTitulo }) => {
  const [dia, setDia] = useState("");
  const [franja, setFranja] = useState("");
  const [aula, setAula] = useState("");
  const [soloLibres, setSoloLibres] = useState(true);
  const [franjas, setFranjas] = useState([]);
  const [aulas, setAulas] = useState([]);
  const [ocupacion, setOcupacion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTitulo && setTitulo("Aulas Libres");
  }, [setTitulo]);

  useEffect(() => {
    fetchFranjasHorarias(setFranjas);
    fetchAulasActivas()
      .then((data) => setAulas(data || []))
      .catch(() => setAulas([]));
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        diaSemanaId: dia || undefined,
        franjaId: franja || undefined,
        aulaId: aula || undefined,
        soloLibres,
      };
      const data = await fetchOcupacionAulas(params);
      setOcupacion(data?.aulas || []);
    } catch (err) {
      console.error("Error cargando ocupación aulas", err);
      setError("No se pudo cargar la ocupación de aulas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dia, franja, aula, soloLibres]);

  const aulasPlanas = useMemo(() => {
    return ocupacion
      .map((aula) =>
        (aula.ocupaciones || []).map((fila) => ({
          ...fila,
          codigo: aula.codigo,
          descripcion: aula.descripcion,
          aulaId: aula.id,
        }))
      )
      .flat()
      .sort((a, b) => {
        const diaA = a?.diaSemana?.orden || 0;
        const diaB = b?.diaSemana?.orden || 0;
        if (diaA !== diaB) return diaA - diaB;
        const frA = a?.franja?.orden || 0;
        const frB = b?.franja?.orden || 0;
        if (frA !== frB) return frA - frB;
        return (a.codigo || "").localeCompare(b.codigo || "");
      });
  }, [ocupacion]);

  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Día</InputLabel>
                <Select value={dia} label="Día" onChange={(e) => setDia(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {DIAS.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Aula</InputLabel>
                <Select value={aula} label="Aula" onChange={(e) => setAula(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {aulas.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.codigo} {a.descripcion ? `- ${a.descripcion}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Franja</InputLabel>
                <Select value={franja} label="Franja" onChange={(e) => setFranja(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {franjas.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {`${f.descripcion} (${f.horaInicio}-${f.horaFin})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={<Switch checked={soloLibres} onChange={(e) => setSoloLibres(e.target.checked)} />}
                label="Solo libres"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress />
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && aulasPlanas.length === 0 && (
        <Alert severity="info">Sin datos para los filtros seleccionados.</Alert>
      )}

      <Grid container spacing={2}>
        {aulasPlanas.map((fila, idx) => (
          <Grid item xs={12} sm={6} md={4} key={`${fila.codigo}-${idx}`}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" component="div">
                  {fila.codigo} <Typography component="span" variant="body2" color="text.secondary">{fila.descripcion}</Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fila.diaSemana?.descripcion} · {fila.franja?.descripcion} ({fila.franja?.horaInicio} - {fila.franja?.horaFin})
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip label={fila.libre ? "Libre" : "Ocupada"} color={fila.libre ? "success" : "error"} size="small" />
                  {fila.grupos && <Chip label={fila.grupos} size="small" />}
                  {fila.profesor && <Chip label={fila.profesor} size="small" variant="outlined" />}
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {fila.detalleCelda || (fila.libre ? "Sin detalle" : "Sin detalle")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AulasLibres;
